<template>
 <div class="">
    <div v-if="!isAdmin" class="alert alert-warning my-3" role="alert" >Please log in as an administrator!</div>
   
    <div v-else class="container">
       <div v-if="errorMsg" class="alert alert-warning my-3" role="alert" >{{errorMsg}}</div>
        <div class="row mt-3">
          <div class="col-8 ">
            <!--<label for="input_usernameorkey" class="form-label">Username or key</label>-->
            <input type="text" class="form-control form-control-sm" id="input_usernameorkey" v-model="usernameOrKey" placeholder="Enter username or key" @keyup.enter="findUser()" :disabled="isLoadingUserInfo"/>
          </div>
          <div class="col-4 d-flex align-items-end justify-content-start gap-1"> 
            <button class="btn btn-sm btn-secondary " type="button" :class="{disabled:!usernameOrKey || isLoadingUserInfo}" @click="findUser()">Search</button>
            <button v-if="user || (findResults && findResults.length)" class="btn btn-sm btn-outline-secondary" type="button" @click="clearUser()" title="Clear and show dashboard"><i class="bi bi-x"></i></button>
          </div>
          <template v-if="findResults && findResults.length"> 
          <div class="col-12 mt-3">
            <h5>Search Results</h5>
            
            <!-- Exact matches -->
            <div v-if="exactMatches.length" class="mb-3">
              <h6 class="text-muted">Exact Matches</h6>
              <div class="list-group">
                <a v-for="result in exactMatches" 
                   :key="result.groupId" 
                   href="#"
                   @click.prevent="selectUser(result.groupId)"
                   class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-semibold">{{ result.username }}</div>
                    <small class="text-muted">{{ result.groupName }}</small>
                    <span v-if="result.isMasterGroup" class="badge bg-warning text-dark ms-1">Master group</span>
                    <span v-if="result.isAdmin" class="badge bg-primary text-light ms-1">Admin</span>
                  </div>
                  <i class="bi bi-chevron-right text-muted"></i>
                </a>
              </div>
            </div>
            
            <!-- Partial matches -->
            <div v-if="partialMatches.length" class="mb-3">
              <h6 class="text-muted">Partial Matches</h6>
              <div class="list-group">
                <a v-for="result in partialMatches" 
                   :key="result.groupId" 
                   href="#"
                   @click.prevent="selectUser(result.groupId)"
                   class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-semibold">{{ result.username }}</div>
                    <small class="text-muted">{{ result.groupName }}</small>
                  </div>
                  <i class="bi bi-chevron-right text-muted"></i>
                </a>
              </div>
            </div>
            
            <!-- Other matches -->
            <div v-if="otherMatches.length" class="mb-3">
              <h6 class="text-muted">Other Matches</h6>
              <div class="list-group">
                <a v-for="result in otherMatches" 
                   :key="result.groupId" 
                   href="#"
                   @click.prevent="selectUser(result.groupId)"
                   class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-semibold">{{ result.username }}</div>
                    <small class="text-muted">{{ result.groupName }} 
                      <span class="badge bg-light border border-dark rounded text-dark ms-1">{{ result.matchType }}</span>
                      <span v-if="result.isMasterGroup" class="badge bg-warning text-dark ms-1">Master group</span>
                      <span v-if="result.isAdmin" class="badge bg-primary text-light ms-1">Admin</span>
                    </small>
                  </div>
                  <i class="bi bi-chevron-right text-muted"></i>
                </a>
              </div>
            </div>
          </div>
        </template>
        <div v-if="isLoadingUserInfo" class="col-12 mt-3 d-flex align-items-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          Loading...
        </div>

        <!-- Signup Dashboard - shown when no user is loaded -->
        <template v-else-if="!user && !findResults?.length">
          <div class="col-12 mt-4">
            <div class="d-flex align-items-center">
              <h5 class="mb-0">Signup Dashboard</h5>
              <button class="btn btn-sm btn-outline-secondary ms-2" @click="loadDashboard" :disabled="isLoadingDashboard" title="Refresh dashboard">
                <i class="bi bi-arrow-clockwise" :class="{'spin': isLoadingDashboard}"></i>
              </button>
            </div>
            <div v-if="isLoadingDashboard" class="d-flex align-items-center mt-2">
              <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
              Loading dashboard...
            </div>
            <div v-else-if="dashboardStats" class="row g-3 mt-1">
              <!-- Today -->
              <div class="col-md-4">
                <div class="card">
                  <div class="card-body text-center">
                    <div class="text-muted fs-sm">New Users Today</div>
                    <div class="fs-3 fw-bold">{{ formatNumber(dashboardStats.today) }}</div>
                    <div :class="changeClass(dashboardStats.today, dashboardStats.todayPrevious)">
                      <i :class="changeIcon(dashboardStats.today, dashboardStats.todayPrevious)"></i>
                      {{ changeText(dashboardStats.today, dashboardStats.todayPrevious) }}
                      <span class="text-muted fs-sm">vs yesterday</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- This Month -->
              <div class="col-md-4">
                <div class="card">
                  <div class="card-body text-center">
                    <div class="text-muted fs-sm">New Users This Month</div>
                    <div class="fs-3 fw-bold">{{ formatNumber(dashboardStats.thisMonth) }}</div>
                    <div :class="changeClass(dashboardStats.thisMonth, dashboardStats.thisMonthPrevious)">
                      <i :class="changeIcon(dashboardStats.thisMonth, dashboardStats.thisMonthPrevious)"></i>
                      {{ changeText(dashboardStats.thisMonth, dashboardStats.thisMonthPrevious) }}
                      <span class="text-muted fs-sm">vs prev month</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Last 90 Days -->
              <div class="col-md-4">
                <div class="card">
                  <div class="card-body text-center">
                    <div class="text-muted fs-sm">New Users Last 90 Days</div>
                    <div class="fs-3 fw-bold">{{ formatNumber(dashboardStats.last90Days) }}</div>
                    <div :class="changeClass(dashboardStats.last90Days, dashboardStats.last90DaysPrevious)">
                      <i :class="changeIcon(dashboardStats.last90Days, dashboardStats.last90DaysPrevious)"></i>
                      {{ changeText(dashboardStats.last90Days, dashboardStats.last90DaysPrevious) }}
                      <span class="text-muted fs-sm">vs prev 90 days</span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Signups Chart -->
              <div class="col-12 mt-3">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="fw-semibold">New Signups</div>
                      <div class="btn-group btn-group-sm" role="group">
                        <button type="button" class="btn" :class="chartPeriod==='day' ? 'btn-primary' : 'btn-outline-secondary'" @click="setChartPeriod('day')">Day</button>
                        <button type="button" class="btn" :class="chartPeriod==='week' ? 'btn-primary' : 'btn-outline-secondary'" @click="setChartPeriod('week')">Week</button>
                        <button type="button" class="btn" :class="chartPeriod==='month' ? 'btn-primary' : 'btn-outline-secondary'" @click="setChartPeriod('month')">Month</button>
                      </div>
                    </div>
                    <svg ref="signupChart" style="width:100%;height:220px;"></svg>
                    <div ref="chartTooltip" class="position-fixed bg-dark text-white px-2 py-1 rounded shadow-sm fs-sm" style="pointer-events:none;opacity:0;z-index:1000;white-space:nowrap;"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="dashboardError" class="alert alert-warning mt-2">{{ dashboardError }}</div>
          </div>
        </template>
        <template v-else-if="user"> 
            <div class="d-flex justify-content-between align-items-center mt-3">
              <span class="fw-semibold">{{ user.userName }}</span>
              <button class="btn btn-sm btn-outline-secondary" @click="copyShareLink()" title="Copy link to this user">
                <i class="bi" :class="copiedShareLink ? 'bi-check-lg text-success' : 'bi-link-45deg'"></i> Share
              </button>
            </div>
            <!-- Vue-controlled Tab Navigation -->
            <nav class="mt-2">
              <div class="nav nav-tabs" role="tablist">
                <a class="nav-link" 
                   :class="{ active: activeTab === 'details' }"
                   href="#" 
                   @click.prevent="setActiveTab('details')"
                   role="tab" 
                   aria-controls="nav-details" 
                   :aria-selected="activeTab === 'details'">
                  Details
                </a>
                <a class="nav-link" 
                   :class="{ active: activeTab === 'stripe' }"
                   href="#" 
                   @click.prevent="setActiveTab('stripe')"
                   role="tab" 
                   aria-controls="nav-stripe" 
                   :aria-selected="activeTab === 'stripe'">
                  Stripe
                </a>
                <a class="nav-link" 
                   :class="{ active: activeTab === 'plans' }"
                   href="#" 
                   @click.prevent="setActiveTab('plans')"
                   role="tab" 
                   aria-controls="nav-plans" 
                   :aria-selected="activeTab === 'plans'">
                  Plans &amp; Properties
                </a>
                <a class="nav-link" 
                   :class="{ active: activeTab === 'children' }"
                   href="#" 
                   @click.prevent="setActiveTab('children')"
                   role="tab" 
                   aria-controls="nav-children" 
                   :aria-selected="activeTab === 'children'">
                  Children
                </a>
                <a class="nav-link" 
                   :class="{ active: activeTab === 'usage' }"
                   href="#" 
                   @click.prevent="setActiveTab('usage')"
                   role="tab" 
                   aria-controls="nav-usage" 
                   :aria-selected="activeTab === 'usage'">
                  Usage
                </a>
                <a class="nav-link" 
                   :class="{ active: activeTab === 'jobs' }"
                   href="#" 
                   @click.prevent="setActiveTab('jobs')"
                   role="tab" 
                   aria-controls="nav-jobs" 
                   :aria-selected="activeTab === 'jobs'">
                  Jobs
                </a>
              </div>
            </nav>

            <!-- Vue-controlled Tab Content -->
            <div class="tab-content" style="overflow-y:auto;overflow-x:clip">
              <!-- Details Tab -->
              <div v-if="activeTab === 'details'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">User details</h5>

                  <div class="col-4">API Key</div>
                  <div class="col-6">{{user.apiKey}}</div>
                  <div class="col-2"><button class="btn btn-sm btn-secondary " type="button" @click="generateNewApiKey()">Regen</button></div>

                  <div class="col-4">Username</div>
                  <div class="col-6"><input type="text" class="form-control form-control-sm" v-model="user.userName" /></div>
                  <div class="col-2"><button class="btn btn-sm btn-secondary " type="button" @click="setUserUsername()">Submit</button></div>

                  <div class="col-4">Execution status</div>
                  <div class="col-6"><input type="text" class="form-control form-control-sm" v-model="user.executionStatus" /></div>
                  <div class="col-2"><button class="btn btn-sm btn-secondary " type="button" @click="setUserExecutionStatus()">Submit</button></div>

                  <div class="col-4">Enabled</div>
                  <div class="col-6 mt-2">
                    <input class="form-check-input" type="checkbox" value="level" id="user_enabled" v-model="user.enabled">
                    <label class="form-check-label" for="user_enabled">Enabled</label>
                  </div>
                  <div class="col-2"><button class="btn btn-sm btn-secondary " type="button" @click="setUserEnabled()">Submit</button></div>

                  <div class="col-4 mt-2">User Id</div><div class="col-6 mt-2">{{userId}}</div>
                  <div class="col-4 mt-2">Master Group Id</div><div class="col-6 mt-2">{{masterGroupId}}</div>

                  <div class="col-4">Use default plan</div>
                  <div class="col-6 mt-2">
                    <input class="form-check-input" type="checkbox" value="level" id="user_isUseDefaultPlan" v-model="userDetails.isUseDefaultPlan">
                    <label class="form-check-label" for="user_isUseDefaultPlan">Enabled</label>
                  </div>
                  <div class="col-2"><button class="btn btn-sm btn-secondary " type="button" @click="setUseDefaultPlan()">Submit</button></div>

                  <template v-if="stripeCustomerInfo">
                    <div class="col-4 mt-2">Stripe Email</div><div class="col-6 mt-2">{{stripeCustomerInfo.email}}</div>
                    <div class="col-4 mt-2">Stripe Id</div><a v-if="stripeUserLink" :href="stripeUserLink" target="_new" class="col-6 mt-2">{{stripeUserId}}</a><div v-else class="col-6 mt-2">-</div>
                  </template>
                  <div v-else-if="isLoadingExtras" class="col-12 mt-2 d-flex align-items-center text-muted"><div class="spinner-border spinner-border-sm me-2" role="status"></div>Loading stripe info...</div>

                  <div class="col-4 mt-2">Plans</div>
                  <div class="col-6 mt-2">
                    <template v-if="hasPlans">
                      <span v-for="(plan, idx) in plans" :key="plan" class="badge bg-secondary me-1">{{plan}}</span>
                    </template>
                    <span v-else class="text-muted">No plans</span>
                  </div>
                </div>
              </div>

              <!-- Stripe Tab -->
              <div v-if="activeTab === 'stripe'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">Stripe info</h5>
                  <div v-if="isLoadingExtras && !stripeCustomerInfo" class="col-12 mt-2 d-flex align-items-center text-muted"><div class="spinner-border spinner-border-sm me-2" role="status"></div>Loading stripe info...</div>
                  <template v-if="stripeCustomerInfo">
                    <div class="col-4 mt-2">Email</div><div class="col-6 mt-2">{{stripeCustomerInfo.email}}</div>
                    <div class="col-4 mt-2">Stripe Id</div><a v-if="stripeUserLink" :href="stripeUserLink" target="_new" class="col-6 mt-2">{{stripeUserId}}</a>
                    <div class="col-4 mt-2">Payment source</div><div class="col-6 mt-2">{{stripeCustomerInfo.defaultSource}}</div>
                    <div class="col-4 mt-2">Domain category</div><div class="col-6 mt-2">{{stripeCustomerInfo.domainCategory}}</div>
                    <div class="col-4 mt-2">Name</div><div class="col-6 mt-2">{{stripeCustomerInfo.name}}</div>
                    <h6 class="col-12 mt-3">Subscriptions</h6>
                    <template v-for="sub in stripeSubscriptions" :key="sub.id">
                        <a v-if="stripeUserLink" :href="'https://dashboard.stripe.com/subscriptions/'+sub.id" target="_new" class="col-4 mt-2">{{sub.product.name}}/{{getPlanName(sub.planId)}}</a>
                        <div class="col-4 mt-2">cancelAtPeriodEnd: {{sub.cancelAtPeriodEnd}}</div>
                        <div class="col-4 mt-2">pastDue: {{sub.pastDue}}</div>
                    </template>
                    <h6 class="col-12 mt-3">Invoices</h6>
                    <template v-for="inv in stripeInvoices" :key="inv.id">
                        <div class="col-3 mt-2">invoiceDate: {{getFormattedDate(inv.invoiceDate)}}</div>
                        <div class="col-3 mt-2">amount: {{inv.amount}}</div>
                        <div class="col-3 mt-2">id: {{inv.id}}</div>
                        <div class="col-3 mt-2">receiptNumber: {{inv.receiptNumber}}</div>
                    </template>
                    <h6 class="col-12 mt-3">Tax Ids</h6>
                    <template v-for="taxId in stripeTaxIds" :key="taxId.id">
                        <div class="col-4 mt-2">id: {{taxId.id}}</div>
                        <div class="col-4 mt-2">type: {{taxId.type}}</div>
                        <div class="col-4 mt-2">country: {{taxId.country}}</div>
                    </template>
                  </template>
                  <div v-else-if="!isLoadingExtras" class="col-10" >No stripe info</div>
                </div>
              </div>

              <!-- Plans Tab -->
              <div v-if="activeTab === 'plans'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">Plans</h5>
                  <div v-if="hasPlans" class="col-10" v-for="plan in plans" :key="plan"><button class="btn btn-sm py-0"  type="button" @click="removePlan(plan)"><i class="bi bi-trash"></i></button>{{plan}}</div>
                  <div v-else class="col-10" >No plans found</div>
                
                  <div class="col-2  d-flex align-items-end justify-content-center"><button class="btn btn-sm btn-secondary " type="button" @click="setUserPlans()">Save</button></div>

                  <h5 class="col-12 mt-3">Resolved plan properties</h5>

                  <div v-if="resolvedPlanProperties" class="table-responsive">
                    <table class="table table-striped table-sm fs-sm">
                      <thead>
                        <tr><th>Property</th><th>Value</th></tr>
                      </thead>
                      <tbody>
                      <tr v-for="key in resolvedPlanPropertiesKeys" :key="key">
                        <td>{{key}}</td>
                        <td>{{resolvedPlanProperties[key]}}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="col-10" >No plan properties</div>
                </div>
              </div>

              <!-- Children Tab -->
              <div v-if="activeTab === 'children'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">Child users</h5>
                  <div v-if="hasClientUsers" class="col-10" v-for="clientUser in clientUsers" :key="clientUser.userName">{{clientUser.userName}}</div>
                  <div v-else class="col-10" >No child users</div>
                </div>
              </div>

              <!-- Usage Tab -->
              <div v-if="activeTab === 'usage'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">Current period usage</h5>
                  <div v-if="isLoadingExtras && !currentPeriodUsage" class="col-12 mt-2 d-flex align-items-center text-muted"><div class="spinner-border spinner-border-sm me-2" role="status"></div>Loading usage data...</div>
                  <template v-if="currentPeriodUsage">
                    <div class="col-4 text-center"><div class="text-muted fs-sm">queryCount</div><div class="">{{formatNumber(currentPeriodUsage.queryCount)}}</div></div>
                    <div class="col-4 text-center"><div class="text-muted fs-sm">cost</div><div class="">{{formatNumber(currentPeriodUsage.cost)}}</div></div>
                    <div class="col-4 text-center"><div class="text-muted fs-sm">failures</div><div class="">{{formatNumber(currentPeriodUsage.failures)}}</div></div>
                    <div class="col-4 text-center mt-2"><div class="text-muted fs-sm">availableCredits</div><div class="">{{formatNumber(currentPeriodUsage.availableCredits)}}</div></div>
                    <div class="col-8 text-center"><div class="text-muted fs-sm">usagePerMinute</div><div class="">{{formatNumber(currentPeriodUsage.usagePerMinute)}}</div></div>
                    <div class="col-4 text-center"><div class="text-muted fs-sm">period</div><div class="">{{currentPeriodUsage.period}}</div></div>
                    <div class="col-4 text-center"><div class="text-muted fs-sm">periodQueryCost</div><div class="">{{formatNumber(currentPeriodUsage.periodQueryCost)}}</div></div>
                    <div class="col-4 text-center"><div class="text-muted fs-sm">periodQueryCount</div><div class="">{{formatNumber(currentPeriodUsage.periodQueryCount)}}</div></div>
                  </template>
                  <div v-else-if="!isLoadingExtras" class="col-10" >No current usage</div>

                  <h5 class="col-12 mt-3">Historical usage</h5>
                  <div v-if="historicalUsage" class="table-responsive">
                    <table class="table table-striped table-sm fs-sm">
                      <thead>
                        <tr><th>Date</th><th>Count</th><th>Cost</th><th>Failures</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="period in historicalUsage.toReversed()" :key="period.period">
                          <td>{{period.period}}</td>
                          <td>{{formatNumber(period.queryCount)}}</td>
                          <td>{{formatNumber(period.cost)}}</td>
                          <td>{{formatNumber(period.failures)}}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="col-10" >No historical usage</div>
                </div>
              </div>

              <!-- Jobs Tab -->
              <div v-if="activeTab === 'jobs'" class="tab-pane show active" role="tabpanel">
                <div class="row">
                  <h5 class="col-12 mt-3">Jobs</h5>
                  
                  <!-- Add checkbox for filtering expired jobs -->
                  <div class="col-12 mb-3">
                    <div class="form-check">
                      <input class="form-check-input" 
                             type="checkbox" 
                             id="showExpiredJobs" 
                             v-model="showExpiredJobs">
                      <label class="form-check-label" for="showExpiredJobs">
                        Show expired jobs
                      </label>
                    </div>
                  </div>
                  
                  <div v-if="isLoadingExtras && !filteredJobInstances" class="col-12 mt-2 d-flex align-items-center text-muted"><div class="spinner-border spinner-border-sm me-2" role="status"></div>Loading jobs...</div>
                  <div v-if="filteredJobInstances" class="table-responsive">
                    <table class="table table-striped table-sm fs-sm">
                      <thead>
                        <tr><th>Id</th><th>Name</th><th>Status</th><th>contentType</th><th>successful/total</th><th>Timing</th><th>Props</th><th>info</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="jobInstance in filteredJobInstances" :key="jobInstance.jobPackage.id">
                          <td>{{jobInstance.jobPackage.id}}</td>
                          <td>{{jobInstance.jobPackage.name}}</td>
                          <template v-if="jobInstance.instance">
                            <td>
                              <span :class="getStatusClass(jobInstance.instance.status)">
                                {{getStatusText(jobInstance.instance.status)}}
                              </span>
                            </td>
                            <td>{{jobInstance.instance.contentType}}</td>
                            <td>{{jobInstance.instance.successful}}/{{jobInstance.instance.total}}</td>
                            <td>
                             <div class="text-nowrap">Start: {{jobInstance.instance.dateTime}}</div>
                             <div class="text-nowrap">Job Start: {{jobInstance.instance.jobStartDateTime}}</div>
                             <div class="text-nowrap">End: {{jobInstance.instance.endDatetime}}<br/></div>
                             <div class="text-nowrap">Expires: {{jobInstance.instance.expirationDateTime}}</div>
                            </td>
                            <td>
                              <div class="text-nowrap">enabled: {{jobInstance.enabled}}</div>
                              <div class="text-nowrap">runonce: {{jobInstance.runonce}}</div>
                              <div class="text-nowrap">hasSchedule: {{jobInstance.hasSchedule}}</div>
                              <div class="text-nowrap">execStatus: {{jobInstance.execStatus}}</div>
                            </td>
                            <td>{{jobInstance.instance.info}}</td>
                          </template>
                          <template v-else>
                            <td colspan="8">No instance info</td>
                          </template>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="col-10">No job instances found</div>
                </div>
              </div>
            </div>
          </template>
        </div>
    </div>
    <CommonModals />
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_serveradmin";
import {VcDateUtils} from "/src/visualcrossing-core/vcdateutils";
import CommonModals from '/src/components/CommonModals.vue';
import * as d3 from 'd3';

export default {
  name: 'loadermonitor',
  props: {
    
  },
  data() {
    return {
      // Vue state for tab management
      activeTab: 'details',
      
      // ... all your existing data properties stay the same
      isLoadingUserInfo:false,
      sessionId:null,
      sessionState:null,
      loaderstatus:null,
      lastrefresh:0,
      refreshinterval:10,
      enableautorefresh:true,
      usernameOrKey:null,
      userDetails:null,
      errorMsg:null,
      allPlans:[],
      findResults:[],
      newPlan:null,
      showExpiredJobs: false,
      copiedShareLink: false,
      dashboardStats: null,
      isLoadingDashboard: false,
      dashboardError: null,
      chartPeriod: 'day',
      isLoadingExtras: false,
    }
  },
  methods:{
    getStatusText(status) {
      const statusMap = {
        1: 'Queued',
        2: 'Running',
        3: 'Complete',
        4: 'Failed',
        5: 'Expired'
      };
      return statusMap[status] || `Status ${status}`;
    },
    
    getStatusClass(status) {
      const classMap = {
        1: 'text-info',
        2: 'text-primary',
        3: 'text-success',
        4: 'text-danger',
        5: 'text-muted'
      };
      return classMap[status] || '';
    },
    
    /**
     * Check URL for search parameter and auto-trigger search if present.
     * Called when session is ready (either on mount or after createsession).
     */
    checkUrlSearchParam() {
      let me = this;
      // Only proceed if we're an admin and don't already have a search in progress
      if (!me.isAdmin || me.isLoadingUserInfo || me.usernameOrKey) return;
      
      const urlParams = new URLSearchParams(window.location.search);
      const searchParam = urlParams.get('search');
      
      if (searchParam) {
        me.usernameOrKey = searchParam;
        me.findUser();
      }
    },
    
    // New Vue tab control method
    setActiveTab(tabName) {
      this.activeTab = tabName;
    },

        // New method to handle user selection from search results
    selectUser(groupId) {
      let me = this;
      me.errorMsg = null;
      me.isLoadingUserInfo = true;
      
      // Minimize/clear the search results
      me.findResults = [];
      
      vcCommunicator.getUserDetails(groupId)
        .then(function (userDetails) {
          me.userDetails = userDetails;
          me.isLoadingUserInfo = false;
          me.activeTab = 'details';
          me.loadUserExtras(groupId);
        })
        .catch((error) => {
          me.userDetails = null;
          console.log(error);
          me.setErrorMsg(error);
          me.isLoadingUserInfo = false;
        });
    },

    // Handle ESC key
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        // Clear search results when ESC is pressed
        this.findResults = [];
      }
    },

    formatNumber(number) {
      let formatter = new Intl.NumberFormat("en-US");
      return formatter.format(number);
    },
    loadDashboard() {
      let me = this;
      if (!me.isAdmin) return;
      me.isLoadingDashboard = true;
      me.dashboardError = null;
      vcCommunicator.getSignupDashboard()
        .then(function(data) {
          me.dashboardStats = data;
          me.isLoadingDashboard = false;
          me.$nextTick(() => me.renderSignupChart());
        })
        .catch(function(error) {
          me.dashboardError = error;
          me.isLoadingDashboard = false;
        });
    },
    changeText(current, previous) {
      if (!previous || previous === 0) return current > 0 ? '+100%' : '0%';
      let pct = ((current - previous) / previous * 100).toFixed(0);
      return (pct >= 0 ? '+' : '') + pct + '%';
    },
    changeClass(current, previous) {
      if (current > previous) return 'text-success fs-sm';
      if (current < previous) return 'text-danger fs-sm';
      return 'text-muted fs-sm';
    },
    changeIcon(current, previous) {
      if (current > previous) return 'bi bi-caret-up-fill';
      if (current < previous) return 'bi bi-caret-down-fill';
      return 'bi bi-dash';
    },
    copyShareLink() {
      let me = this;
      if (!me.shareLink) return;
      navigator.clipboard.writeText(me.shareLink).then(function() {
        me.copiedShareLink = true;
        setTimeout(function() { me.copiedShareLink = false; }, 2000);
      });
    },
    clearUser() {
      let me = this;
      me.userDetails = null;
      me.findResults = [];
      me.usernameOrKey = null;
      me.errorMsg = null;
      me.loadDashboard();
    },
    loadUserExtras(criteria) {
      let me = this;
      if (!me.userDetails) return;
      me.isLoadingExtras = true;
      let p1 = vcCommunicator.getUserStripeInfo(criteria)
        .then(function(data) { if (me.userDetails) me.userDetails.stripeCustomerInfo = data; })
        .catch(function(e) { console.log('stripe info error', e); });
      let p2 = vcCommunicator.getUserUsageSummary(criteria)
        .then(function(data) { if (me.userDetails) me.userDetails.usageSummary = data; })
        .catch(function(e) { console.log('usage summary error', e); });
      let p3 = vcCommunicator.getUserJobInstances(criteria)
        .then(function(data) { if (me.userDetails) me.userDetails.jobInstances = data; })
        .catch(function(e) { console.log('job instances error', e); });
      Promise.allSettled([p1, p2, p3]).then(function() { me.isLoadingExtras = false; });
    },
    setChartPeriod(period) {
      this.chartPeriod = period;
      this.renderSignupChart();
    },
    renderSignupChart() {
      let me = this;
      if (!me.dashboardStats || !me.dashboardStats.dailyCounts) return;
      let svgEl = me.$refs.signupChart;
      if (!svgEl) return;

      let daily = me.dashboardStats.dailyCounts;
      let entries = Object.keys(daily).sort().map(k => ({ date: new Date(k + 'T00:00:00'), count: daily[k] }));
      if (!entries.length) return;

      let data;
      let tickFormat;
      if (me.chartPeriod === 'week') {
        let grouped = d3.groups(entries, d => d3.timeMonday(d.date).getTime());
        data = grouped.map(([weekTs, vals]) => ({ label: new Date(weekTs), count: d3.sum(vals, v => v.count) }));
        tickFormat = d3.timeFormat('%b %d');
      } else if (me.chartPeriod === 'month') {
        let grouped = d3.groups(entries, d => d3.timeMonth(d.date).getTime());
        data = grouped.map(([monthTs, vals]) => ({ label: new Date(monthTs), count: d3.sum(vals, v => v.count) }));
        tickFormat = d3.timeFormat('%b %Y');
      } else {
        data = entries.map(d => ({ label: d.date, count: d.count }));
        tickFormat = d3.timeFormat('%b %d');
      }

      let svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      let rect = svgEl.getBoundingClientRect();
      let w = rect.width || 600;
      let h = rect.height || 220;
      let margin = { top: 10, right: 15, bottom: 35, left: 40 };
      let iw = w - margin.left - margin.right;
      let ih = h - margin.top - margin.bottom;

      let g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      let x = d3.scaleBand().domain(data.map(d => d.label)).range([0, iw]).padding(0.2);
      let y = d3.scaleLinear().domain([0, d3.max(data, d => d.count) || 1]).nice().range([ih, 0]);

      let maxTicks = me.chartPeriod === 'month' ? data.length : 15;
      g.append('g')
        .attr('transform', `translate(0,${ih})`)
        .call(d3.axisBottom(x).tickFormat(tickFormat).tickValues(
          data.length > maxTicks ? x.domain().filter((d, i) => i % Math.ceil(data.length / maxTicks) === 0) : x.domain()
        ))
        .selectAll('text').attr('transform', 'rotate(-35)').style('text-anchor', 'end').style('font-size', '10px');

      g.append('g').call(d3.axisLeft(y).ticks(5));

      let tooltip = d3.select(me.$refs.chartTooltip);

      g.selectAll('.bar').data(data).enter().append('rect')
        .attr('class', 'bar')
        .attr('x', d => x(d.label))
        .attr('y', d => y(d.count))
        .attr('width', x.bandwidth())
        .attr('height', d => ih - y(d.count))
        .attr('fill', '#0d6efd')
        .attr('rx', 2)
        .on('mouseover', function(event, d) {
          d3.select(this).attr('fill', '#0a58ca');
          tooltip.style('opacity', 1)
            .html('<strong>' + tickFormat(d.label) + '</strong><br/>' + d.count + ' signups');
        })
        .on('mousemove', function(event) {
          tooltip.style('left', (event.clientX + 12) + 'px')
            .style('top', (event.clientY - 28) + 'px');
        })
        .on('mouseout', function() {
          d3.select(this).attr('fill', '#0d6efd');
          tooltip.style('opacity', 0);
        });
    },
    setUserPlans(plan) {
      let me=this;
      let plansList=me.userDetails.plans.reduce((a,d)=>{
        if (!a) return d;
        return a+","+d;
      }, null)
      vcCommunicator.setUserPlans(me.userDetails.masterGroupId, plansList) 
        .then(function () {
          //noop?
        })
        .catch((error) => {
          me.allPlans=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    removePlan(plan) {
      let me=this;
      if (!me.userDetails.plans) return;
      let index=me.userDetails.plans.indexOf(plan);
      while (index!==-1) {
          me.userDetails.plans.splice(index,1);
          index=me.userDetails.plans.indexOf(plan);
      }
    },
    refreshAllPlans() {
      let me=this;
      vcCommunicator.getAllPlans() 
        .then(function (allPlans) {
          me.allPlans=allPlans;
        })
        .catch((error) => {
          me.allPlans=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    generateNewApiKey:function() {
      let me=this;
      if (!me.user) return;
       me.errorMsg=null;
      vcCommunicator.generateNewApiKey(me.userId) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    setUserUsername:function() {
      let me=this;
      if (!me.user) return;
       me.errorMsg=null;
      vcCommunicator.setUserUsername(me.userId, me.user.userName) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    setUserExecutionStatus:function() {
      let me=this;
      if (!me.user) return;
       me.errorMsg=null;
      vcCommunicator.setUserExecutionStatus(me.userId, me.user.executionStatus) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    setUserEnabled:function() {
      let me=this;
      if (!me.user) return;
       me.errorMsg=null;
      vcCommunicator.setUserEnabled(me.userId, me.user.enabled) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    setUseDefaultPlan:function() {
      let me=this;
      if (!me.user) return;
       me.errorMsg=null;
      vcCommunicator.setUseDefaultPlan(me.userId, me.userDetails.isUseDefaultPlan) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error);
        });
    },
    setErrorMsg(msg) {
      let me=this;
      me.errorMsg=msg;
      setTimeout(function() {
        me.errorMsg=null;
      }, 10000);
    },
    findUser:function() {
      let me=this;
      let usernameOrKey=me.usernameOrKey;
      me.errorMsg=null;
      me.isLoadingUserInfo=true;
      me.userDetails=null;
      vcCommunicator.findUser(me.usernameOrKey) //
        .then(function (findResults) {
          me.findResults=findResults && findResults.results;
          me.isLoadingUserInfo=false;

          if (me.findResults && me.findResults.length===1) {
            // Automatically select the user if only one result found
            me.selectUser(me.findResults[0].groupId);
          } else {
            me.setErrorMsg ("No users found matching '"+usernameOrKey+"'");
          }
        })
        .catch((error) => {
          me.findResults=null;
          console.log(error);
          me.setErrorMsg (error);
          me.isLoadingUserInfo=false;
        });
    },
    getUserDetails:function() {
      let me=this;
        me.errorMsg=null;
      let usernameOrKey=me.usernameOrKey;
      if (!me.sessionState || !usernameOrKey) return;
      me.isLoadingUserInfo=true;
      vcCommunicator.getUserDetails(usernameOrKey) //
        .then(function (userDetails) {
          me.userDetails=userDetails;
          me.isLoadingUserInfo=false;
          me.loadUserExtras(usernameOrKey);
        })
        .catch((error) => {
          me.userDetails=null;
          console.log(error);
          me.setErrorMsg (error)  ;
          me.isLoadingUserInfo=false;
        });
    },
    getPlanName(planId) {
      let me=this;
      let plan=me.getPlan(planId);
      if (!plan) return planId+"?";
      return plan.name || plan.interval;
    },
    getPlan(planId) {
      let me=this;
      var products=me.stripeCustomerInfo && me.stripeCustomerInfo.products;
      if (!products) return null;
      return products.reduce(function (a, d) {
        if (a || !d || !d.plans) return a;
        return d.plans.reduce(function(b,e) {
          if (b ) return b;
          return (e && e.id===planId && e) || null;
        }, a)
      }, null);
    },
    getFormattedDate(dateTime) {
      var date=dateTime && new Date(dateTime)
      if (!date) return dateTime+"?";
      return VcDateUtils.formatDate(date);
    },
  },
  computed:{
    filteredJobInstances() {
      const me = this;
      if (!me.jobInstances) return null;
      
      // If showExpiredJobs is true, show all jobs
      if (me.showExpiredJobs) {
        return me.jobInstances;
      }
      
      // Otherwise, filter out jobs with status = 5 (expired)
      return me.jobInstances.filter(jobInstance => {
        return !jobInstance.instance || jobInstance.instance.status !== 5;
      });
    },
    exactMatches() {
      return this.findResults.filter(r => r.matchType === 'exact');
    },
    
    partialMatches() {
      return this.findResults.filter(r => r.matchType === 'partial');
    },
    
    otherMatches() {
      return this.findResults.filter(r => r.matchType !== 'exact' && r.matchType !== 'partial');
    },
    stripeCustomerInfo() {
      let me=this;
      return me.userDetails && me.userDetails.stripeCustomerInfo;
    },
    stripeSubscriptions() {
      let me=this;
      return me.stripeCustomerInfo && me.stripeCustomerInfo.subscriptions;
    },
    stripeTaxIds() {
      let me=this;
      return me.stripeCustomerInfo && me.stripeCustomerInfo.taxIds;
    },
    stripeInvoices() {
      let me=this;
      return me.stripeCustomerInfo && me.stripeCustomerInfo.invoices;
    },
    currentPeriodUsage() {
       let me=this;
       return me.userDetails && me.userDetails.usageSummary && me.userDetails.usageSummary.currentPeriodUsage;
    },
    historicalUsage() {
       let me=this;
       return me.userDetails && me.userDetails.usageSummary && me.userDetails.usageSummary.historicalUsage;
    },
    jobInstances() {
       let me=this;
       return me.userDetails && me.userDetails.jobInstances;
    },
    possibleNewPlans() {
      let me=this;
      if (!me.allPlans) return null;
      if (!me.plans) return me.allPlans;
      let filtered= me.allPlans.filter(function(d) {
        return me.plans.indexOf(d)===-1;
      });
      return filtered;
    },
    userId() {
      let me=this;
      return me.userDetails && me.userDetails.id;
    },
    shareLink() {
      if (!this.userId) return null;
      let base = window.location.origin + window.location.pathname;
      return base + '?search=' + encodeURIComponent(this.userId);
    },
    stripeUserId() {
      let me=this;
      return me.userDetails && me.userDetails.masterGroupStripeId;
    },
    masterGroupId() {
      let me=this;
      return me.userDetails && me.userDetails.masterGroupId;
    },
    stripeUserLink() {
      let me=this;
      return me.stripeUserId && "https://dashboard.stripe.com/customers/"+me.stripeUserId;
    },
    user() {
      let me=this;
      return me.userDetails && me.userDetails.user;
    },
    hasPlans() {
      let me=this;
      return me.plans && me.plans.length;
    },
    planDetail() {
      let me=this;
      return me.userDetails && me.userDetails.planDetail
    },
    plans() {
      let me=this;
      return me.planDetail && me.planDetail.plans;
    },
    resolvedPlanProperties() {
      let me=this;
      return me.planDetail && me.planDetail.resolvedPlanProperties;
    },
    resolvedPlanPropertiesKeys() {
      let me=this;
      let keys= me.resolvedPlanProperties && Object.keys(me.resolvedPlanProperties);
      if (keys) keys.sort();
      return keys;
    },
    hasClientUsers() {
      let me=this;
      return me.clientUsers && me.clientUsers.length;
    },
    clientUsers() {
      let me=this;
      return me.userDetails && me.userDetails.clientUsers;
    },
    rawData() {
      let me=this;
      return JSON.stringify(me.userDetails)
    },

    isAdmin() {
      let me=this;
      return me.sessionState && me.sessionState.contextProperties &&  me.sessionState.contextProperties.isAdmin==="true";
    }
  },
  watch: {
     // Reset to first tab when new user is loaded
     user: {
       handler(newUser) {
         if (newUser) {
           this.activeTab = 'details';
         }
       }
     },

     newPlan:{
      handler:function(newValue,oldValue) {
        let me=this;
        if (!newValue) return;
        if (!me.userDetails.plans) me.userDetails.plans=[];
        me.userDetails.plans.push(newValue);
        me.newPlan=null;
      },
      deep: true
    },
  },
   unmounted() {
    var me=this;
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  mounted() {
     var me=this;

    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeyDown);
   
    var vcCommunicator=window.vcCommunicator;
    if (!vcCommunicator) {
      console.log("No vcCommunicator");
      return;
    }
    me.sessionId=vcCommunicator.sessionId;
    me.sessionState=vcCommunicator.sessionState;
    
    // Check URL for search parameter and auto-search if session is ready
    if (me.sessionState && me.isAdmin) {
      me.checkUrlSearchParam();
      me.loadDashboard();
    }
    
    vcCommunicator.addHandler('createsession', function (sessionId,sessionState) {
      me.sessionId=sessionId;
      me.sessionState=sessionState;
      me.refreshAllPlans();
      // Check URL for search parameter after session is established
      me.checkUrlSearchParam();
      me.loadDashboard();
    });
     vcCommunicator.addHandler('terminatesession', function () {
       me.sessionId=null;
       me.sessionState=null;
    });
  },
  components: {
    CommonModals
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/*
  .old {
      font-style: italic;
      opacity: 0.9;
  }
  .table-striped tbody  .error {
    font-weight: bold;
  }
  .table-striped tbody .error td {
      background-color: rgb(245, 212, 212) !important;
  }
  */
  /* Ensure active tab styling */
  /*
  .nav-link.active {
    color: #0d6efd !important;
    background-color: #fff !important;
    border-color: #dee2e6 #dee2e6 #fff !important;
  }
  */
  /* Tab pane styling */
  /*
  .tab-pane.active {
    display: block !important;
  }
    */
  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
</style>